/**
 * Vista: Impresión de Check List de Ensamble y Funcionamiento
 * Formato: AMX-PR-FR-02_R9
 * Template HTML optimizado para impresión (2 páginas)
 *
 * Pie de documento: tabla footer sin fondo gris; columna "Página X de 2" con rowspan=2 (footer-page-col).
 * Títulos de categoría (p. ej. INSPECCIÓN FRONTAL): primera fila de items-table con celdas C/I vacías + cat-title-cell.
 * Detalle: docs/CHANGELOG_KAREN.md [2026-04-08] (Formato de impresión).
 */

import produccionService from '../services/produccion.service.js';

export default {
  async render() {
    return `
      <div class="loading-container" style="text-align: center; padding: 3rem;">
        <div class="spinner"></div>
        <p>Cargando formato para impresión...</p>
      </div>
    `;
  },

  async afterRender() {
    const path = window.location.pathname;
    const id = path.split('/')[3]; // /produccion/checklists/:id/print

    try {
      const [response, plantillaResponse] = await Promise.all([
        produccionService.obtenerChecklist(id),
        produccionService.obtenerPlantillaChecklist().catch(() => ({ data: {} }))
      ]);
      const checklist = response.data || response;
      const plantilla = plantillaResponse.data || plantillaResponse || {};
      this.renderPrint(checklist, plantilla);

      // Auto-abrir diálogo de impresión
      setTimeout(() => {
        window.print();
      }, 500);
    } catch (error) {
      console.error('Error:', error);
      document.getElementById('content').innerHTML = `
        <div style="padding: 2rem; text-align: center;">
          <h2>Error al cargar check list</h2>
          <p>No se pudo cargar la información para imprimir.</p>
          <button onclick="window.history.back()" class="btn btn-secondary">Volver</button>
        </div>
      `;
    }
  },

  // Mapeo nivel_visual → segmento (1-7)
  NIVEL_A_SEGMENTO: {
    'min': 1, 'bajo': 2, 'medio_bajo': 3, 'medio': 4, 'medio_alto': 5, 'alto': 6, 'max': 7
  },

  // Colores por segmento para cada fluido
  FLUIDOS_COLORES: {
    combustible:               { 1:'#dc3545', 4:'#ffc107', 7:'#28a745' },
    anticongelante:            { 1:'#dc3545', 4:'#28a745', 5:'#28a745', 7:'#dc3545' },
    aceite_sistema_hidraulico: { 1:'#dc3545', 4:'#28a745', 5:'#28a745', 7:'#dc3545' },
    aceite_motor:              { 1:'#dc3545', 4:'#28a745', 5:'#28a745', 7:'#dc3545' },
    aceite_transmision:        { 1:'#dc3545', 4:'#28a745', 5:'#28a745', 7:'#dc3545' },
    aceite_sistema_direccion:  { 1:'#dc3545', 4:'#28a745', 5:'#28a745', 7:'#dc3545' },
    aceite_frenos:             { 1:'#dc3545', 4:'#28a745', 5:'#28a745', 7:'#dc3545' },
    aceite_eje_delantero:      { 1:'#dc3545', 4:'#28a745', 5:'#28a745', 7:'#dc3545' },
    aceite_maza_izquierda:     { 1:'#dc3545', 4:'#28a745', 7:'#dc3545' },
    aceite_maza_derecha:       { 1:'#dc3545', 4:'#28a745', 7:'#dc3545' },
    aceite_filtro_aire:        { 1:'#dc3545', 4:'#28a745', 5:'#28a745', 7:'#dc3545' }
  },

  /**
   * Renderizar barra de nivel de fluido con 7 segmentos.
   * noAplica=true → el nivel no aplica para el modelo (config dinámica en `modelos`).
   */
  renderNivelBar(fluidoKey, label, nivelVisual, noAplica = false) {
    const segmentoActivo = this.NIVEL_A_SEGMENTO[nivelVisual] || 0;
    const colores = this.FLUIDOS_COLORES[fluidoKey] || {};

    let segments = '';
    for (let i = 1; i <= 7; i++) {
      const isActive = segmentoActivo === i;
      const bgColor = colores[i] || '#fff';
      const mark = isActive ? 'X' : '';
      segments += `<td class="nivel-seg" style="background:${bgColor};">${mark}</td>`;
    }

    // Marca de agua diagonal "NO APLICA" SOBRE la barra (conserva su diseño/colores).
    const watermark = noAplica ? `
      <div style="position:absolute;top:0;left:0;right:0;bottom:0;display:flex;align-items:center;justify-content:center;pointer-events:none;">
        <span style="transform:rotate(-14deg);font-size:8px;font-weight:bold;color:rgba(220,53,69,0.85);background:rgba(255,255,255,0.6);border:1px solid rgba(220,53,69,0.85);padding:0.5px 5px;border-radius:3px;letter-spacing:0.5px;white-space:nowrap;">NO APLICA</span>
      </div>` : '';

    return `
      <div class="nivel-item" style="position:relative;">
        <div class="nivel-label">${label}</div>
        <table class="nivel-table">
          <tr>${segments}</tr>
          <tr>
            <td class="nivel-minmax-cell">Min</td>
            <td colspan="5"></td>
            <td class="nivel-minmax-cell" style="text-align:right;">Max</td>
          </tr>
        </table>
        ${watermark}
      </div>
    `;
  },

  /**
   * Combinar plantilla con items guardados del checklist.
   * Devuelve objeto { categoria: [{ item, estado, observacion }] }
   * Siempre muestra todos los items de la plantilla, con estado si existe.
   */
  mergeItemsConPlantilla(itemsPorCategoria, plantilla) {
    const resultado = {};
    const categorias = Object.keys(plantilla);

    for (const cat of categorias) {
      const itemsPlantilla = plantilla[cat] || [];
      const itemsGuardados = itemsPorCategoria[cat] || [];

      resultado[cat] = itemsPlantilla.map(textoPlantilla => {
        // Buscar si existe un item guardado que coincida
        const guardado = itemsGuardados.find(ig => ig.item === textoPlantilla);
        return {
          item: textoPlantilla,
          estado: guardado ? guardado.estado : null,
          observacion: guardado ? guardado.observacion : null,
          valores_inline: guardado ? guardado.valores_inline : null
        };
      });
    }

    return resultado;
  },

  /**
   * Mismo criterio que ChecklistDetalle: codigos de modelo y/o palabra Serie dentro de ( ... )
   */
  _highlightRefsModeloHTML(plain) {
    if (plain == null || plain === '') return '';
    const esc = String(plain)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    return esc.replace(/\([^)]+\)/g, (full) => {
      const inner = full.slice(1, -1);
      const tieneCodigoModelo = /[A-Z]{2,4}\d{3,4}/.test(inner);
      const tieneSerie = /\bserie\b/i.test(inner);
      if (tieneCodigoModelo || tieneSerie) {
        return `<span style="color:#dc3545;font-weight:600;">${full}</span>`;
      }
      return full;
    });
  },

  /**
   * Sustituir ___ por valores guardados; resalta referencias de modelo entre paréntesis en rojo
   */
  renderItemText(item) {
    const texto = item.item || '';

    const valorSeguro = (v) =>
      String(v || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

    if (!texto.includes('___') || !item.valores_inline) {
      return this._highlightRefsModeloHTML(texto);
    }

    let valores;
    if (Array.isArray(item.valores_inline)) {
      valores = item.valores_inline;
    } else {
      try {
        const parsed = JSON.parse(item.valores_inline);
        if (Array.isArray(parsed)) valores = parsed;
      } catch (e) { /* no es JSON */ }
    }

    if (!valores) return this._highlightRefsModeloHTML(texto);

    let idx = 0;
    const parts = texto.split(/___+/);
    return parts
      .map((seg, pi) => {
        const h = this._highlightRefsModeloHTML(seg);
        if (pi === parts.length - 1) return h;
        const val = valores[idx++] || '';
        const inner = val
          ? `<span style="font-weight:bold; text-decoration:underline;">${valorSeguro(val)}</span>`
          : '___';
        return h + inner;
      })
      .join('');
  },

  /**
   * Renderizar items de una categoría como filas con checkboxes C / I
   */
  renderCategoriaItems(titulo, items) {
    if (!items || items.length === 0) return '';

    const rows = items.map(item => {
      const cChecked = item.estado === 'corregir';
      const iChecked = item.estado === 'ideal';
      const noAplica = item.estado === 'no_aplica';

      const cBox = `<td class="chk-cell"><div class="chk ${cChecked ? 'chk-c' : ''}">${cChecked ? 'X' : ''}</div></td>`;
      const iBox = `<td class="chk-cell"><div class="chk ${iChecked ? 'chk-i' : ''}">${iChecked ? 'X' : ''}</div></td>`;

      const textoConValores = this.renderItemText(item);
      const texto = noAplica ? `<span class="item-na">${textoConValores}</span>` : textoConValores;

      return `<tr>
        ${cBox}${iBox}
        <td class="item-text">${texto}</td>
      </tr>`;
    }).join('');

    // Titulo alineado con la columna de texto (misma sangria que los renglones C/I)
    const titleRow = titulo
      ? `<tr class="cat-title-row"><td class="chk-cell cat-title-chk-spacer"></td><td class="chk-cell cat-title-chk-spacer"></td><td class="cat-title-cell">${titulo}</td></tr>`
      : '';

    return `
      <div class="cat-section">
        <table class="items-table">
          ${titleRow}
          ${rows}
        </table>
      </div>
    `;
  },

  /**
   * Render principal del formato de impresión (2 páginas)
   */
  renderPrint(c, plantilla = {}) {
    const html = this.generateHTML(c, plantilla);
    document.getElementById('content').innerHTML = html;
  },

  /**
   * Genera el HTML completo del formato de impresión (con estilos)
   * Retorna el string HTML para uso externo (ej: descarga PDF)
   */
  generateHTML(c, plantilla = {}) {
    const fp = (c.fecha || '').substring(0, 10).split('-');
    const fecha = fp.length === 3 ? `${fp[2]}/${fp[1]}/${fp[0]}` : '';
    const items = this.mergeItemsConPlantilla(c.items_por_categoria || {}, plantilla);
    const niveles = c.niveles || {};
    const torques = c.torques || {};

    const getNivel = (key) => niveles[key] || null;
    // Config dinámica: niveles que no aplican para el modelo (resuelta por el backend).
    const nivelesNA = Array.isArray(c.niveles_no_aplican) ? c.niveles_no_aplican : [];
    const na = (key) => nivelesNA.includes(key);

    const html = `
      <div class="print-cl-container">
        <!-- ==================== PÁGINA 1: VERIFICACIÓN DE ENSAMBLE ==================== -->
        <div class="print-page page-1">
          <!-- Header -->
          <div class="print-header">
            <img src="/img/Logo _LOVOL.png" alt="LOVOL" class="logo-left" onerror="this.style.display='none'" />
            <h1 class="print-title">CHECK LIST DE ENSAMBLE Y FUNCIONAMIENTO DE TRACTOR</h1>
            <img src="/img/Logo_AGRO.png" alt="AGROMX" class="logo-right" onerror="this.style.display='none'" />
          </div>

          <!-- Datos principales -->
          <div class="datos-grid">
            <div class="dato-row">
              <div class="dato-field"><span class="dato-label">NUM. SERIE:</span><span class="dato-value">${c.num_serie || ''}</span></div>
              <div class="dato-field"><span class="dato-label">MODELO:</span><span class="dato-value">${c.modelo || ''}</span></div>
            </div>
            <div class="dato-row">
              <div class="dato-field"><span class="dato-label">HORAS MOTOR:</span><span class="dato-value">${c.horas_motor || ''}</span></div>
              <div class="dato-field"><span class="dato-label">FECHA:</span><span class="dato-value">${fecha}</span></div>
            </div>
          </div>

          <!-- Título sección -->
          <div class="section-title">VERIFICACION DE ENSAMBLE</div>

          <!-- Leyenda -->
          <div class="leyenda">
            <span class="leyenda-item"><span class="leyenda-box leyenda-c">C</span> CORREGIR</span>
            <span class="leyenda-item"><span class="leyenda-box leyenda-i">I</span> IDEAL</span>
            <span class="leyenda-item"><span class="leyenda-dash">&mdash;</span> NO APLICA</span>
          </div>

          <!-- Contenido 3 columnas -->
          <div class="page1-layout">
            <!-- Columna izquierda -->
            <div class="p1-col-left">
              <div class="col-header">
                <span class="col-hdr-c">C</span>
                <span class="col-hdr-i">I</span>
                <span class="col-hdr-label">PUNTO DE INSPECCIÓN</span>
              </div>
              ${this.renderCategoriaItems('INSPECCIÓN FRONTAL', items.inspeccion_frontal)}
              ${this.renderCategoriaItems('INSPECCIÓN TRASERA', (items.inspeccion_trasera || []).slice(0, -3))}
            </div>

            <!-- Columna centro -->
            <div class="p1-col-center">
              <div class="col-header">
                <span class="col-hdr-c">C</span>
                <span class="col-hdr-i">I</span>
                <span class="col-hdr-label">PUNTO DE INSPECCIÓN</span>
              </div>
              ${this.renderCategoriaItems('', (items.inspeccion_trasera || []).slice(-3))}
              ${this.renderCategoriaItems('INSPECCIÓN EN LA PLATAFORMA', items.inspeccion_plataforma)}
              ${this.renderCategoriaItems('ETIQUETAS', items.etiquetas)}
              ${this.renderCategoriaItems('ENGRASADO', items.engrasado)}
              ${this.renderCategoriaItems('INSPECCIÓN FILTROS', items.inspeccion_filtros)}
              ${this.renderCategoriaItems('ACCESORIOS', items.accesorios)}
            </div>

            <!-- Columna derecha: Torques + Niveles de fluidos -->
            <div class="p1-col-right">
              <!-- Diagrama de torque -->
              <div class="torque-print-grid">
                <div class="torque-val torque-left">
                  <div><span class="torque-num">${torques.frontal_izq_nm || '___'}</span> Nm</div>
                  <div><span class="torque-num">${torques.frontal_izq_psi || '___'}</span> PSI</div>
                </div>
                <img src="/img/tractor-neumatico.png" alt="Tractor" class="torque-tractor-img" onerror="this.style.display='none'">
                <div class="torque-val torque-right">
                  <div><span class="torque-num">${torques.frontal_der_nm || '___'}</span> Nm</div>
                  <div><span class="torque-num">${torques.frontal_der_psi || '___'}</span> PSI</div>
                </div>
                <div class="torque-val torque-left">
                  <div><span class="torque-num">${torques.trasero_izq_nm || '___'}</span> Nm</div>
                  <div><span class="torque-num">${torques.trasero_izq_psi || '___'}</span> PSI</div>
                </div>
                <div class="torque-val torque-right">
                  <div><span class="torque-num">${torques.trasero_der_nm || '___'}</span> Nm</div>
                  <div><span class="torque-num">${torques.trasero_der_psi || '___'}</span> PSI</div>
                </div>
              </div>
              <div class="niveles-col">
                ${this.renderNivelBar('combustible', 'NIVEL DE COMBUSTIBLE *', getNivel('combustible'), na('combustible'))}
                ${this.renderNivelBar('aceite_sistema_hidraulico', 'NIVEL DE ACEITE DE SISTEMA HIDRÁULICO', getNivel('aceite_sistema_hidraulico'), na('aceite_sistema_hidraulico'))}
                ${this.renderNivelBar('aceite_motor', 'NIVEL DE ACEITE DE MOTOR', getNivel('aceite_motor'), na('aceite_motor'))}
                ${this.renderNivelBar('aceite_transmision', 'NIVEL DE ACEITE DE TRANSMISIÓN', getNivel('aceite_transmision'), na('aceite_transmision'))}
                ${this.renderNivelBar('aceite_sistema_direccion', 'NIVEL DE ACEITE DE SISTEMA DE DIRECCIÓN', getNivel('aceite_sistema_direccion'), na('aceite_sistema_direccion'))}
                ${this.renderNivelBar('aceite_eje_delantero', 'NIVEL DE ACEITE DEL EJE DELANTERO', getNivel('aceite_eje_delantero'), na('aceite_eje_delantero'))}
                ${this.renderNivelBar('anticongelante', 'NIVEL DE ANTICONGELANTE', getNivel('anticongelante'), na('anticongelante'))}
                ${this.renderNivelBar('aceite_frenos', 'NIVEL DE ACEITE DE FRENOS', getNivel('aceite_frenos'), na('aceite_frenos'))}
                ${this.renderNivelBar('aceite_maza_izquierda', 'NIVEL DE ACEITE DE MAZA IZQUIERDA', getNivel('aceite_maza_izquierda'), na('aceite_maza_izquierda'))}
                ${this.renderNivelBar('aceite_maza_derecha', 'NIVEL DE ACEITE DE MAZA DERECHA', getNivel('aceite_maza_derecha'), na('aceite_maza_derecha'))}
                ${this.renderNivelBar('aceite_filtro_aire', 'NIVEL DE ACEITE DEL FILTRO DE AIRE', getNivel('aceite_filtro_aire'), na('aceite_filtro_aire'))}
              </div>
              <div class="niveles-nota">* Para tractores de demostración el nivel de combustible debe ser medio tanque o más.</div>
            </div>
          </div>

          <!-- Pie página 1: numeración en una sola celda (rowspan) para no mostrar línea horizontal en esa columna -->
          <div class="doc-footer">
            <table class="footer-table">
              <tr>
                <th>CÓDIGO</th><th>REVISIÓN</th><th>FECHA DE PUBLICACIÓN</th><th>SUSTITUYE</th><th>VIGENCIA</th>
                <td rowspan="2" class="footer-page-col">Página 1 de 2</td>
              </tr>
              <tr>
                <td>AMX-PR-FR-02_R9</td><td>9</td><td>Agosto 2025</td><td>AMX-PR-FR-02_R8</td><td>Agosto 2025 al 2027</td>
              </tr>
            </table>
          </div>
        </div>

        <!-- ==================== PÁGINA 2: VERIFICACIÓN DE FUNCIONAMIENTO ==================== -->
        <div class="print-page page-2">
          <!-- Título -->
          <div class="section-title" style="margin-top:0;">VERIFICACIÓN DE FUNCIONAMIENTO</div>
          <div class="p2-nota">PREVIO A LA REALIZACIÓN DE ÉSTA INSPECCIÓN SE DEBE DEJAR ACELERADO EL TRACTOR POR 15 MINUTOS A 1750~2000 RPM</div>

          <!-- Leyenda -->
          <div class="leyenda">
            <span class="leyenda-item"><span class="leyenda-box leyenda-c">C</span> CORREGIR</span>
            <span class="leyenda-item"><span class="leyenda-box leyenda-i">I</span> IDEAL</span>
            <span class="leyenda-item"><span class="leyenda-dash">&mdash;</span> NO APLICA</span>
          </div>

          <!-- Contenido 3 columnas -->
          <div class="page2-layout">
            <!-- Columna izquierda -->
            <div class="p2-col-left">
              <div class="col-header">
                <span class="col-hdr-c">C</span>
                <span class="col-hdr-i">I</span>
                <span class="col-hdr-label">MOTOR</span>
              </div>
              ${this.renderCategoriaItems('', items.motor)}
              ${this.renderCategoriaItems('TREN MOTRIZ', items.tren_motriz)}
              ${this.renderCategoriaItems('FRENOS', items.frenos)}
              ${this.renderCategoriaItems('SISTEMA DE DIRECCIÓN', items.sistema_direccion)}
              ${this.renderCategoriaItems('SISTEMA HIDRÁULICO', items.sistema_hidraulico)}
            </div>

            <!-- Columna centro -->
            <div class="p2-col-center">
              <div class="col-header">
                <span class="col-hdr-c">C</span>
                <span class="col-hdr-i">I</span>
                <span class="col-hdr-label">SISTEMA ELÉCTRICO</span>
              </div>
              ${this.renderCategoriaItems('', items.sistema_electrico)}
              ${this.renderCategoriaItems('PLATAFORMA DE OPERADOR', items.plataforma_operador)}
            </div>

            <!-- Columna derecha -->
            <div class="p2-col-right">
              <div class="col-header">
                <span class="col-hdr-c">C</span>
                <span class="col-hdr-i">I</span>
                <span class="col-hdr-label">PINTURA</span>
              </div>
              ${this.renderCategoriaItems('NEGRA', items.pintura_negra)}
              ${this.renderCategoriaItems('AZUL', items.pintura_azul)}
              ${this.renderCategoriaItems('GRIS', items.pintura_gris)}
              ${this.renderCategoriaItems('ENGOMADOS', items.engomados)}
              ${this.renderCategoriaItems('LIMPIEZA', items.limpieza)}
              ${this.renderCategoriaItems('ABRILLANTADOR', items.abrillantador)}
            </div>
          </div>

          <!-- Observaciones -->
          <div class="observaciones-section">
            <div class="obs-line-full">${c.observaciones || ''}</div>
            <div class="obs-line-empty"></div>
            <div class="obs-line-empty"></div>
            <div class="obs-line-empty"></div>
          </div>

          <!-- Producto No Conforme -->
          <div class="pnc-section">
            <div class="pnc-title">SÍ SE DIAGNOSTICA ALGÚN PRODUCTO NO CONFORME (PNC)</div>
            ${this.renderCategoriaItems('', items.producto_no_conforme)}
          </div>

          <!-- Firmas -->
          <div style="display:flex; justify-content:space-between; margin-top:1mm; gap:5mm;">
            <div style="flex:1;">
              <div style="height:12mm; display:flex; align-items:flex-end; justify-content:center;">
                ${c.tecnico_ensamble_firma_url ? `<img src="${c.tecnico_ensamble_firma_url}" style="max-width:100%; max-height:10mm; object-fit:contain;" onerror="this.style.display='none'">` : ''}
              </div>
              ${c.tecnico_ensamble_nombre || c.tecnico_ensamble ? `<div style="font-size:7pt; text-align:center;">${c.tecnico_ensamble_nombre || c.tecnico_ensamble}</div>` : ''}
              <div style="border-top:1px solid #000; width:100%;"></div>
              <div style="font-size:6pt; margin-top:0.5mm; text-align:center;"><span style="font-weight:bold; color:#000;">TÉCNICO DE PRODUCCIÓN</span></div>
              <div style="font-size:6pt; text-align:center; color:#000; font-style:italic;">Nombre y firma</div>
            </div>
            <div style="flex:1;">
              <div style="height:12mm; display:flex; align-items:flex-end; justify-content:center;">
                ${c.responsable_linea_firma_url ? `<img src="${c.responsable_linea_firma_url}" style="max-width:100%; max-height:10mm; object-fit:contain;" onerror="this.style.display='none'">` : ''}
              </div>
              ${c.responsable_linea_nombre || c.responsable_linea ? `<div style="font-size:7pt; text-align:center;">${c.responsable_linea_nombre || c.responsable_linea}</div>` : ''}
              <div style="border-top:1px solid #000; width:100%;"></div>
              <div style="font-size:6pt; margin-top:0.5mm; text-align:center;"><span style="font-weight:bold; color:#000;">RESPONSABLE DE LÍNEA DE PRODUCTO</span></div>
              <div style="font-size:6pt; text-align:center; color:#000; font-style:italic;">Nombre y firma</div>
            </div>
            <div style="flex:1;">
              <div style="height:12mm; display:flex; align-items:flex-end; justify-content:center;">
                ${c.responsable_area_firma_url ? `<img src="${c.responsable_area_firma_url}" style="max-width:100%; max-height:10mm; object-fit:contain;" onerror="this.style.display='none'">` : ''}
              </div>
              ${c.responsable_area_nombre || c.responsable_area ? `<div style="font-size:7pt; text-align:center;">${c.responsable_area_nombre || c.responsable_area}</div>` : ''}
              <div style="border-top:1px solid #000; width:100%;"></div>
              <div style="font-size:6pt; margin-top:0.5mm; text-align:center;"><span style="font-weight:bold; color:#000;">RESPONSABLE DE ÁREA DE PRODUCCIÓN</span></div>
              <div style="font-size:6pt; text-align:center; color:#000; font-style:italic;">Nombre y firma</div>
            </div>
          </div>

          <!-- Notas al pie -->
          <div class="notas-pie">
            <p>1.- Todos los puntos se refieren a una inspección, verificación, ajuste o limpieza según sea el caso.</p>
            <p>2.- Los puntos de verificación no marcados se considerarán como no aplicables para el modelo del tractor.</p>
            <p>3.- Para la especificación de parámetros de medición (torque, distancia, rpm, peso, presión y voltaje) referirse al checklist AMX-PR-FR-08 FICHA DE PREVIA LIBERACIÓN.</p>
          </div>

          <!-- Pie página 2: misma celda unificada para "Página 2 de 2" -->
          <div class="doc-footer">
            <table class="footer-table">
              <tr>
                <th>CÓDIGO</th><th>REVISIÓN</th><th>FECHA DE PUBLICACIÓN</th><th>SUSTITUYE</th><th>VIGENCIA</th>
                <td rowspan="2" class="footer-page-col">Página 2 de 2</td>
              </tr>
              <tr>
                <td>AMX-PR-FR-02_R9</td><td>9</td><td>Agosto 2025</td><td>AMX-PR-FR-02_R8</td><td>Agosto 2025 al 2027</td>
              </tr>
            </table>
          </div>
        </div>
      </div>

      <style>
        /* ==================== ESTILOS GENERALES ==================== */
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: Arial, sans-serif;
          font-size: 7pt;
          background: #f0f0f0;
        }

        .print-cl-container {
          width: 297mm;
          margin: 0 auto;
        }

        .print-page {
          background: white;
          width: 297mm;
          padding: 6mm 6mm;
          margin-bottom: 10mm;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          position: relative;
          box-sizing: border-box;
        }

        /* ==================== HEADER ==================== */
        .print-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 2mm;
          margin-bottom: 4mm;
        }

        /* Logos: object-fit contain; tamano un poco mayor que la version compacta */
        .print-header .logo-left,
        .print-header .logo-right {
          height: 11mm;
          max-width: 36mm;
          width: auto;
          object-fit: contain;
          object-position: center center;
          flex-shrink: 0;
          display: block;
        }

        /* Titulo una sola linea; barra azul ancha entre logos (flex:1) */
        .print-title {
          font-size: 12pt;
          font-weight: bold;
          color: #fff;
          background: #4371C4;
          padding: 2mm 5mm;
          text-align: center;
          white-space: nowrap;
          flex: 1 1 auto;
          min-width: 0;
          margin: 0;
          line-height: 1.25;
          print-color-adjust: exact !important;
          -webkit-print-color-adjust: exact !important;
        }

        /* ==================== DATOS PRINCIPALES ==================== */
        .datos-grid {
          margin-bottom: 3mm;
        }

        .dato-row {
          display: flex;
          gap: 5mm;
          margin-bottom: 1.5mm;
        }

        .dato-field {
          flex: 1;
          display: flex;
          align-items: baseline;
        }

        .dato-label {
          font-weight: normal;
          font-size: 7pt;
          margin-right: 1mm;
          white-space: nowrap;
        }

        .dato-value {
          flex: 1;
          border-bottom: 1px solid #333;
          font-size: 7pt;
          min-height: 4mm;
          font-weight: bold;
        }

        /* ==================== LEYENDA ==================== */
        .leyenda {
          display: flex;
          justify-content: center;
          gap: 15mm;
          margin-bottom: 0.5mm;
          font-size: 4pt;
          align-items: center;
        }

        .leyenda-item {
          display: flex;
          align-items: center;
          gap: 1mm;
        }

        .leyenda-box {
          width: 2mm;
          height: 2mm;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 3.5pt;
          font-weight: bold;
          color: #fff;
          print-color-adjust: exact !important;
          -webkit-print-color-adjust: exact !important;
        }

        .leyenda-c {
          background: #dc3545;
        }

        .leyenda-i {
          background: #28a745;
        }

        .leyenda-dash {
          font-weight: bold;
          font-size: 5pt;
        }

        /* ==================== SECCIÓN TÍTULO ==================== */
        .section-title {
          text-align: center;
          font-weight: bold;
          font-size: 10pt;
          margin: 2mm 0;
        }

        .p2-nota {
          text-align: center;
          font-size: 6pt;
          font-weight: bold;
          margin-bottom: 2mm;
        }

        /* ==================== LAYOUT PÁGINA 1 (3 columnas) ==================== */
        /* space-between reparte huecos entre columnas; sin margen negativo (evita solapar centro con torque) */
        .page1-layout {
          display: flex;
          width: 100%;
          box-sizing: border-box;
          gap: 3mm;
          align-items: flex-start;
          justify-content: space-between;
        }

        .p1-col-left {
          flex: 0 1 auto;
          min-width: 0;
          width: max-content;
          max-width: 46%;
        }

        .p1-col-center {
          flex: 0 1 auto;
          min-width: 0;
          width: max-content;
          max-width: 46%;
        }

        .p1-col-right {
          flex: 0 0 auto;
          width: 44mm;
        }

        .page1-layout .items-table {
          width: auto;
          max-width: 100%;
          table-layout: auto;
        }

        /* ==================== LAYOUT PÁGINA 2 (3 columnas) ==================== */
        .page2-layout {
          display: flex;
          width: 100%;
          box-sizing: border-box;
          gap: 3mm;
          margin-bottom: 3mm;
          align-items: flex-start;
          justify-content: space-between;
        }

        .p2-col-left {
          flex: 0.9 1 0;
          min-width: 0;
        }

        .p2-col-center {
          flex: 1 1 0;
          min-width: 0;
        }

        .p2-col-right {
          flex: 1.1 1 0;
          min-width: 0;
        }

        /* ==================== COLUMN HEADER ==================== */
        .col-header {
          display: flex;
          align-items: center;
          gap: 0.5mm;
          margin-bottom: 0.5mm;
          padding-bottom: 0.3mm;
          border-bottom: none;
          font-size: 5pt;
          font-weight: bold;
        }

        .col-hdr-c {
          width: 2.5mm;
          height: 2.5mm;
          background: #dc3545;
          color: #fff;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 4pt;
          print-color-adjust: exact !important;
          -webkit-print-color-adjust: exact !important;
        }

        .col-hdr-i {
          width: 2.5mm;
          height: 2.5mm;
          background: #28a745;
          color: #fff;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 4pt;
          print-color-adjust: exact !important;
          -webkit-print-color-adjust: exact !important;
        }

        .col-hdr-label {
          font-size: 5pt;
        }

        /* ==================== CATEGORÍAS E ITEMS ==================== */
        .cat-section {
          margin-bottom: 0.8mm;
        }

        .cat-title-row td {
          border: none;
          vertical-align: bottom;
        }

        .cat-title-row .cat-title-chk-spacer {
          padding: 0;
        }

        .cat-title-cell {
          font-weight: bold;
          font-size: 5pt;
          padding: 0.1mm 0.5mm 0.25mm 0.5mm;
          line-height: 1.1;
        }

        .items-table {
          width: 100%;
          border-collapse: collapse;
        }

        .items-table tr {
          line-height: 1.05;
        }

        .chk-cell {
          width: 3mm;
          padding: 0.1mm 0.2mm;
          text-align: center;
          vertical-align: top;
        }

        .chk {
          width: 2.5mm;
          height: 2.5mm;
          border: 0.5px solid #333;
          display: block;
          text-align: center;
          line-height: 2.5mm;
          font-size: 0;
          box-sizing: border-box;
        }

        .chk-c {
          background: #dc3545;
          color: #fff;
          print-color-adjust: exact !important;
          -webkit-print-color-adjust: exact !important;
        }

        .chk-i {
          background: #28a745;
          color: #fff;
          print-color-adjust: exact !important;
          -webkit-print-color-adjust: exact !important;
        }

        .item-text {
          font-size: 5pt;
          padding: 0.1mm 0.5mm;
          vertical-align: top;
          line-height: 1.1;
        }

        .item-na {
          text-decoration: line-through;
          color: #999;
        }

        /* ==================== DIAGRAMA DE TORQUE ==================== */
        .torque-print-grid {
          display: grid;
          grid-template-columns: minmax(0, 0.75fr) auto minmax(0, 1.25fr);
          grid-template-rows: 1fr 1fr;
          align-items: center;
          margin-bottom: 1mm;
          margin-top: -1mm;
          margin-left: 0;
          padding: 0;
          gap: 0.5mm 0.5mm;
        }

        .torque-tractor-img {
          grid-row: 1 / 3;
          grid-column: 2;
          max-height: 22mm;
          object-fit: contain;
        }

        .torque-val {
          font-size: 4pt;
          line-height: 1.3;
        }

        .torque-left { text-align: right; }
        .torque-right { text-align: left; }

        .torque-num {
          font-weight: bold;
          text-decoration: underline;
        }

        /* ==================== NIVELES DE FLUIDOS ==================== */
        .niveles-col {
          display: flex;
          flex-direction: column;
          gap: 1mm;
        }

        .nivel-item {
          display: flex;
          flex-direction: column;
        }

        .nivel-label {
          font-size: 4pt;
          font-weight: bold;
          margin-bottom: 0.3mm;
          line-height: 1.05;
        }

        .nivel-table {
          border-collapse: collapse;
        }

        .nivel-seg {
          width: 3mm;
          height: 3mm;
          border: 0.5px solid #333;
          text-align: center;
          font-size: 4pt;
          font-weight: bold;
          padding: 0;
          line-height: 3mm;
          print-color-adjust: exact !important;
          -webkit-print-color-adjust: exact !important;
        }

        .nivel-minmax-cell {
          font-size: 3.5pt;
          color: #666;
          border: none !important;
          padding: 0 !important;
        }

        .niveles-nota {
          font-size: 4pt;
          color: #dc3545;
          margin-top: 1mm;
          font-weight: bold;
          text-align: center;
        }

        /* ==================== OBSERVACIONES ==================== */
        .observaciones-section {
          margin: 0.5mm 0;
        }

        .obs-line-full {
          border-bottom: 1px solid #000;
          min-height: 3.5mm;
          font-size: 5pt;
          padding: 0.5mm 0;
        }

        .obs-line-empty {
          border-bottom: 1px solid #000;
          min-height: 3.5mm;
        }

        /* ==================== PRODUCTO NO CONFORME ==================== */
        .pnc-section {
          margin: 0.5mm 0;
          padding: 1mm;
        }

        .pnc-title {
          font-weight: bold;
          font-size: 5.5pt;
          margin-bottom: 1mm;
        }

        .pnc-item {
          display: flex;
          align-items: center;
          gap: 1.5mm;
          font-size: 5pt;
          margin-bottom: 0.5mm;
        }

        /* ==================== FIRMAS ==================== */
        .firmas-row {
          display: flex;
          justify-content: space-between;
          margin-top: 10mm;
          gap: 5mm;
        }

        .firma-box {
          flex: 1;
          text-align: center;
        }

        .firma-img {
          width: 100%;
          max-height: 15mm;
          object-fit: contain;
          display: block;
          margin: 0 auto 1mm;
        }

        .firma-name {
          font-size: 7pt;
          margin-bottom: 1mm;
        }

        .firma-line {
          border-top: 1px solid #000;
          width: 100%;
        }

        .firma-role {
          font-size: 6pt;
          font-weight: bold;
          margin-top: 1mm;
        }

        .firma-sub {
          font-size: 6pt;
          font-style: italic;
          color: #666;
        }

        /* ==================== NOTAS AL PIE ==================== */
        .notas-pie {
          margin-top: 2mm;
          font-size: 4.5pt;
          line-height: 1.3;
        }

        /* ==================== FOOTER DOCUMENTO ==================== */
        .doc-footer {
          margin-top: 1mm;
        }

        .footer-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 6pt;
        }

        .footer-table th,
        .footer-table td {
          border: 1px solid #000;
          padding: 1mm 2mm;
          text-align: center;
          vertical-align: middle;
          background: #fff;
        }

        /* Encabezados del pie sin relleno (ahorro de tinta / legibilidad en papel) */
        .footer-table th {
          background: transparent;
          font-weight: bold;
        }

        /* Columna de numeración: mismo criterio que formato impreso (caja, texto centrado, sin negrita en el número) */
        .footer-table .footer-page-col {
          width: 18%;
          min-width: 22mm;
          font-weight: normal;
          background: #fff !important;
        }

        /* ==================== PRINT STYLES ==================== */
        @media print {
          body {
            background: white !important;
            padding: 0 !important;
            margin: 0 !important;
          }

          .navbar, nav, .sidebar, aside, #loading, #toast-container, #modal-container, .no-print {
            display: none !important;
          }

          .main-layout, #app, #content, .content {
            display: block !important;
            margin: 0 !important;
            padding: 0 !important;
            max-width: 100% !important;
          }

          .print-cl-container {
            width: 100% !important;
          }

          .print-page {
            box-shadow: none !important;
            margin: 0 !important;
            padding: 6mm 6mm !important;
            width: 100% !important;
            height: auto !important;
            overflow: visible !important;
            page-break-after: always;
          }

          .print-page:last-child {
            page-break-after: avoid;
          }

          @page {
            size: landscape;
            margin: 5mm;
          }

          .footer-table th {
            background: transparent !important;
          }

          .footer-table .footer-page-col {
            background: #fff !important;
          }

          /* Mantener colores en impresión */
          .print-title,
          .leyenda-c,
          .leyenda-i,
          .col-hdr-c,
          .col-hdr-i,
          .chk-c,
          .chk-i,
          .nivel-seg {
            print-color-adjust: exact !important;
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }

          .page1-layout,
          .page2-layout {
            display: flex !important;
          }

          .page1-layout {
            width: 100% !important;
            gap: 3mm !important;
            justify-content: space-between !important;
          }

          .p1-col-left,
          .p1-col-center,
          .p1-col-right {
            margin-left: 0 !important;
            margin-right: 0 !important;
          }

          .page2-layout {
            width: 100% !important;
            gap: 3mm !important;
            justify-content: space-between !important;
          }

          .p2-col-left,
          .p2-col-center,
          .p2-col-right {
            margin-left: 0 !important;
            margin-right: 0 !important;
          }

          .page1-layout .items-table {
            width: auto !important;
          }
        }
      </style>
    `;

    return html;
  }
};
